import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const getGalleryImages = () => [
  { id: 1, url: "/images/haircut_pompadour_fade.jpg", category: "fade", tall: true },
  { id: 2, url: "/images/haircut_fade_beard.jpg", category: "beard", tall: false },
  { id: 3, url: "/images/haircut_classic_side.jpg", category: "classic", tall: false },
  { id: 4, url: "/images/beard_trim_detail.jpg", category: "beard", tall: true },
  { id: 5, url: "/images/haircut_fade_profile.jpg", category: "fade", tall: false },
  { id: 6, url: "/images/haircut_textured.jpg", category: "modern", tall: false },
  { id: 7, url: "/images/haircut_pompadour_style.jpg", category: "modern", tall: true },
  { id: 8, url: "/images/haircut_slicked_back.jpg", category: "classic", tall: false },
  { id: 9, url: "/images/haircut_clean_fade.jpg", category: "fade", tall: false },
  { id: 10, url: "/images/orkhan_cutting.jpg", category: "classic", tall: false },
  { id: 11, url: "/images/haircut_nape_detail.jpg", category: "fade", tall: true },
  { id: 12, url: "/images/haircut_side_profile.jpg", category: "modern", tall: false },
];

export default function Gallery({ t }) {
  const [active, setActive] = useState("all");
  const [lightbox, setLightbox] = useState(null);
  const images = getGalleryImages();
  const altTexts = t.gallery.altTexts;

  const filtered =
    active === "all" ? images : images.filter((img) => img.category === active);

  const step = (d) =>
    setLightbox((i) => (i === null ? i : (i + d + filtered.length) % filtered.length));

  useEffect(() => {
    if (lightbox === null) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, filtered.length]);

  const filters = [
    { key: "all", label: t.gallery.filters.all },
    { key: "fade", label: t.gallery.filters.fade },
    { key: "beard", label: t.gallery.filters.beard },
    { key: "classic", label: t.gallery.filters.classic },
    { key: "modern", label: t.gallery.filters.modern },
  ];

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="py-20 md:py-32 bg-[#050505]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-12"
        >
          <p className="text-xs tracking-[0.3em] sm:tracking-[0.38em] uppercase text-[#D4AF37] font-body mb-4">
            {t.gallery.sectionTitle}
          </p>
          <h2
            className="text-4xl md:text-5xl font-bold text-white font-heading"
            data-testid="gallery-title"
          >
            {t.gallery.headline}
          </h2>
        </motion.div>

        {/* Filter tabs */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.12 }}
          className="flex flex-wrap justify-center gap-2 mb-12"
          data-testid="gallery-filters"
        >
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActive(filter.key)}
              className={`px-5 sm:px-6 min-h-[44px] text-xs tracking-[0.18em] uppercase font-body font-medium border transition-colors duration-300 ${
                active === filter.key
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "border-white/20 text-white/65 hover:border-[#D4AF37]/40 hover:text-white/75"
              }`}
              data-testid={`filter-${filter.key}`}
            >
              {filter.label}
            </button>
          ))}
        </motion.div>

        {/* Gallery masonry */}
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={active}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="columns-2 md:columns-3 gap-3 md:gap-4"
            data-testid="gallery-grid"
          >
            {filtered.map((img, i) => (
              <button
                type="button"
                key={img.id}
                onClick={() => setLightbox(i)}
                aria-label={altTexts[img.id - 1] || "Haircut"}
                className="block w-full text-left break-inside-avoid mb-3 md:mb-4 overflow-hidden group relative cursor-zoom-in"
                data-testid={`gallery-item-${img.id}`}
              >
                <img
                  src={img.url}
                  alt={altTexts[img.id - 1] || ""}
                  width="600"
                  height="800"
                  className={`w-full object-cover group-hover:scale-105 transition-transform duration-700 ${
                    img.tall || i % 4 === 0 ? "h-72 md:h-96" : "h-52 md:h-64"
                  }`}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors duration-300 flex items-end p-5">
                  <span className="text-white text-xs uppercase tracking-[0.22em] opacity-0 group-hover:opacity-100 transition-opacity duration-300 font-body">
                    {filters.find(f => f.key === img.category)?.label || img.category}
                  </span>
                </div>
              </button>
            ))}
          </motion.div>
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {lightbox !== null && filtered[lightbox] && (
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[80] bg-black/95 flex items-center justify-center p-4"
            onClick={() => setLightbox(null)}
            data-testid="gallery-lightbox"
          >
            <button type="button" aria-label="Close" onClick={() => setLightbox(null)}
              className="absolute top-4 right-4 w-11 h-11 flex items-center justify-center text-white hover:text-[#D4AF37]">
              <X size={26} />
            </button>
            <button type="button" aria-label="Previous" onClick={(e) => { e.stopPropagation(); step(-1); }}
              className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white hover:text-[#D4AF37]">
              <ChevronLeft size={30} />
            </button>
            <img
              src={filtered[lightbox].url}
              alt={altTexts[filtered[lightbox].id - 1] || ""}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[88vh] max-w-full object-contain"
            />
            <button type="button" aria-label="Next" onClick={(e) => { e.stopPropagation(); step(1); }}
              className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center text-white hover:text-[#D4AF37]">
              <ChevronRight size={30} />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
